import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { useLanguage } from "@/hooks/useLanguage";
import { useInviteToken } from "@/hooks/useInviteToken";
import DomesticRegisterPanel from "@/components/auth/DomesticRegisterPanel";
import InternationalRegisterPanel from "@/components/auth/InternationalRegisterPanel";
import InvitationBanner from "@/components/invitation/InvitationBanner";
import { Loader2 } from "lucide-react";

/**
 * 前台应用注册页面
 * Vercel 风格设计
 * 支持邀请链接注册（invite_token）
 *
 * 根据系统运营区域配置显示不同的注册面板：
 * - 国内模式 (domestic): 显示手机号注册，隐藏语言切换器，强制中文
 * - 海外模式 (international): 显示邮箱注册，显示语言切换器
 */
export default function Register() {
  const { t } = useTranslation("register");
  const { isDomestic, loading: regionLoading } = useOperationRegion();
  const { changeLanguage } = useLanguage();
  const location = useLocation();
  const {
    inviteToken,
    isValid: inviteValid,
    inviter,
    rewardPoints,
    loading: inviteLoading,
  } = useInviteToken();

  // 获取重定向目标路径（优先从 state，其次从 URL 参数）
  const urlParams = new URLSearchParams(location.search);
  const fromParam = urlParams.get("from");
  const from = (location.state as any)?.from?.pathname || fromParam || "/";

  // 国内模式强制设置语言为中文
  useEffect(() => {
    if (isDomestic) {
      changeLanguage("zh");
    }
  }, [isDomestic, changeLanguage]);

  // 综合加载状态
  const loading = regionLoading || inviteLoading;

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-[360px] flex flex-col -mt-16 sm:mt-0">
        {loading ? (
          <div className="min-h-[60vh] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/images/black-logo-notext.svg"
                alt="Tunzo"
                className="h-12 w-12 dark:hidden"
              />
              <img
                src="/images/white-logo-notext.svg"
                alt="Tunzo"
                className="h-12 w-12 hidden dark:block"
              />
            </div>

            {/* 标题区域 */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-2">
                {t("title")}
              </h1>
              <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            </div>

            {/* 邀请信息横幅 */}
            <InvitationBanner
              inviter={inviter}
              rewardPoints={rewardPoints}
              isValid={inviteValid}
              loading={inviteLoading}
            />

            {/* 注册面板：根据运营区域显示对应面板 */}
            {isDomestic ? (
              <DomesticRegisterPanel
                inviteToken={inviteToken}
                redirectTo={from}
              />
            ) : (
              <InternationalRegisterPanel
                inviteToken={inviteToken}
                redirectTo={from}
              />
            )}

            {/* 登录链接 */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              {t("common.hasAccount")}{" "}
              <Link
                to={
                  from !== "/"
                    ? `/login?from=${encodeURIComponent(from)}`
                    : "/login"
                }
                className="text-foreground font-medium hover:text-primary transition-colors"
              >
                {t("common.login")}
              </Link>
            </p>
          </>
        )}
      </div>

      {/* 底部条款 - 已隐藏 */}
      <footer className="hidden fixed bottom-0 left-0 right-0 py-6 px-4 text-center text-xs text-muted-foreground">
        <p>
          {t("app.registerAgreement")}{" "}
          <Link to="/terms" className="hover:text-foreground transition-colors">
            {t("app.terms")}
          </Link>
          {t("app.agreementMiddle")}{" "}
          <Link
            to="/privacy"
            className="hover:text-foreground transition-colors"
          >
            {t("app.privacy")}
          </Link>
        </p>
      </footer>
    </div>
  );
}
